<template>
  <nav v-if="hasArchive" class="edition-switcher" :aria-label="$t('pastEditions.toggle')" @keydown="onKeydown"
    @focusout="onFocusOut">
    <button ref="toggle" type="button" class="edition-toggle" :class="{ open: isOpen }" :aria-expanded="isOpen"
      :aria-controls="listId" @click="toggle">
      {{ $t('pastEditions.toggle') }}
      <span class="pi" :class="isOpen ? 'pi-angle-up' : 'pi-angle-down'" aria-hidden="true"></span>
    </button>

    <ul v-show="isOpen" :id="listId" ref="list" class="edition-list">
      <li v-for="item in items" :key="item.year">
        <a :href="item.url" class="edition-link" :class="{ active: item.isActive }"
          :aria-current="item.isActive ? 'page' : null">
          {{ item.label }}
        </a>
      </li>
    </ul>
  </nav>
</template>

<script>
import {
  ARCHIVE_YEARS,
  CURRENT_YEAR,
  EDITION_KEY,
  editionUrl,
} from "@/editions";

let instanceCount = 0;

export default {
  name: "EditionSwitcher",
  inject: {
    edition: { from: EDITION_KEY },
  },
  data() {
    instanceCount += 1;
    return {
      isOpen: false,
      listId: `edition-list-${instanceCount}`,
    };
  },
  computed: {
    hasArchive() {
      return ARCHIVE_YEARS.length > 0;
    },
    items() {
      const current = {
        year: CURRENT_YEAR,
        label: this.$t("pastEditions.current", { year: CURRENT_YEAR }),
      };
      const archive = ARCHIVE_YEARS.map((year) => ({
        year,
        label: String(year),
      }));
      return [current, ...archive].map((item) => ({
        ...item,
        url: editionUrl(item.year),
        isActive: item.year === this.edition.year,
      }));
    },
  },
  beforeUnmount() {
    document.removeEventListener("pointerdown", this.onDocumentPointerDown);
  },
  methods: {
    links() {
      return this.$refs.list
        ? Array.from(this.$refs.list.querySelectorAll(".edition-link"))
        : [];
    },
    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      document.addEventListener("pointerdown", this.onDocumentPointerDown);
    },
    close({ returnFocus = false } = {}) {
      if (!this.isOpen) return;
      this.isOpen = false;
      document.removeEventListener("pointerdown", this.onDocumentPointerDown);
      if (returnFocus) this.$refs.toggle.focus();
    },
    toggle() {
      if (this.isOpen) this.close();
      else this.open();
    },
    async focusLink(index) {
      this.open();
      await this.$nextTick();
      const links = this.links();
      if (links.length === 0) return;
      links[(index + links.length) % links.length].focus();
    },
    onKeydown(event) {
      const links = this.links();
      const current = links.indexOf(document.activeElement);
      const onToggle = event.target === this.$refs.toggle;

      switch (event.key) {
        case "Escape":
          if (!this.isOpen) return;
          this.close({ returnFocus: true });
          break;
        case "ArrowDown":
          this.focusLink(onToggle || current === -1 ? 0 : current + 1);
          break;
        case "ArrowUp":
          this.focusLink(onToggle || current === -1 ? -1 : current - 1);
          break;
        case "Home":
          if (current === -1) return;
          this.focusLink(0);
          break;
        case "End":
          if (current === -1) return;
          this.focusLink(-1);
          break;
        default:
          return;
      }
      event.preventDefault();
    },
    // Zavření
    onFocusOut(event) {
      const next = event.relatedTarget;
      if (next && !this.$el.contains(next)) this.close();
    },
    // Zavření při kliknutí / dotyku mimo komponentu
    onDocumentPointerDown(event) {
      if (!this.$el.contains(event.target)) this.close();
    },
  },
};
</script>

<style scoped>
.edition-switcher {
  position: relative;
  display: inline-block;
  vertical-align: middle;
  margin-left: 20px;
  font-family: var(--font-mono);
  font-size: 14px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.edition-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  background: none;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 0 10px;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: var(--text-secondary);
  cursor: pointer;
  transition: color 0.3s ease, border-color 0.3s ease, text-shadow 0.3s ease;
}

.edition-toggle:hover,
.edition-toggle.open {
  color: var(--accent-neon);
  text-shadow: 0 0 8px rgba(0, 240, 255, 0.5);
}

.edition-toggle:focus-visible {
  outline: none;
  border-color: var(--accent-neon);
}

.edition-list {
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  min-width: 200px;
  max-width: calc(100vw - 32px);
  margin: 0;
  padding: 6px;
  list-style: none;
  text-align: left;
  background: var(--bg-panel);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid var(--glass-border);
  border-radius: 8px;
  box-shadow: var(--glass-shadow);
}

.edition-link {
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid transparent;
  border-radius: 4px;
  color: var(--text-primary);
  white-space: nowrap;
  text-decoration: none;
}

.edition-link:hover,
.edition-link:focus-visible {
  outline: none;
  color: var(--accent-neon);
  border-color: var(--glass-border);
  background: rgba(255, 255, 255, 0.04);
}

.edition-link.active {
  color: var(--accent-primary);
  border-color: var(--accent-primary);
  text-shadow: 0 0 10px rgba(255, 152, 0, 0.3);
}
</style>
